import { useEffect } from "react";

export interface DocumentMeta {
  title: string;
  description: string;
  url: string;
  image: string;
  type?: "website" | "article";
  publishedTime?: string;
  modifiedTime?: string;
}

type Restore = () => void;

function setMeta(attr: "name" | "property", key: string, value: string): Restore {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`
  );
  const created = !el;
  const previous = el?.getAttribute("content") ?? null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
  return () => {
    if (created) el!.remove();
    else if (previous !== null) el!.setAttribute("content", previous);
  };
}

function setCanonical(href: string): Restore {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  const created = !el;
  const previous = el?.getAttribute("href") ?? null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return () => {
    if (created) el!.remove();
    else if (previous !== null) el!.setAttribute("href", previous);
  };
}

/** Sets per-route head tags and restores the previous values on unmount. */
export function useDocumentMeta(meta: DocumentMeta | null): void {
  const {
    title,
    description,
    url,
    image,
    type = "website",
    publishedTime,
    modifiedTime,
  } = meta ?? ({} as Partial<DocumentMeta>);

  useEffect(() => {
    if (!title || !description || !url || !image) return;

    const previousTitle = document.title;
    document.title = title;

    const restores: Restore[] = [
      setMeta("name", "description", description),
      setCanonical(url),
      setMeta("property", "og:type", type),
      setMeta("property", "og:title", title),
      setMeta("property", "og:description", description),
      setMeta("property", "og:url", url),
      setMeta("property", "og:image", image),
      setMeta("name", "twitter:url", url),
      setMeta("name", "twitter:title", title),
      setMeta("name", "twitter:description", description),
      setMeta("name", "twitter:image", image),
    ];
    if (publishedTime)
      restores.push(setMeta("property", "article:published_time", publishedTime));
    if (modifiedTime)
      restores.push(setMeta("property", "article:modified_time", modifiedTime));

    return () => {
      document.title = previousTitle;
      restores.reverse().forEach((r) => r());
    };
  }, [title, description, url, image, type, publishedTime, modifiedTime]);
}
